import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  DocumentSnapshot,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where
} from 'firebase/firestore';
import { db, resolveStudentClassId } from '../../../../../utils/firebase';
import { PET_CONSUMABLE_CATEGORY, PET_PLAY_TICKET_CATEGORY, type OwnedPetConsumable, type OwnedPetPlayTicket, type PetItemEffect, type PetPlayActionType } from '../../../../../types/petStore';
import { DEFAULT_VIRTUAL_PET_SETTINGS, PET_BALANCE } from '../config/petBalance';
import {
  applyElapsedDecay,
  normalizeDailyActions,
  normalizeMood
} from '../domain/petEngine';
import {
  DungeonDifficulty,
  PetDungeonRun,
  PetEvolutionState,
  PetInventory,
  PetProfile,
  PetShareCard,
  PetStats,
  VirtualPetClassSettings
} from '../types';

interface StudentPath {
  schoolId: string;
  classId: string;
  /** Firestore 학생 문서 ID */
  uid: string;
}

interface ResolveStudentPathParams {
  authUid: string;
  schoolId: string;
  classId?: string;
  grade?: number;
  class?: number;
}

const clamp = (value: number, min = 0, max = 100) => Math.max(min, Math.min(max, value));

const safeGetDoc = async (ref: ReturnType<typeof doc>): Promise<DocumentSnapshot | null> => {
  try {
    return await getDoc(ref);
  } catch (error) {
    console.warn('VirtualPet getDoc failed:', ref.path, error);
    return null;
  }
};

const buildClassId = (schoolId: string, grade?: number, classNum?: number, hintClassId?: string) => {
  const trimmed = hintClassId?.trim();
  if (trimmed) return trimmed;
  if (grade != null && classNum != null) {
    return `${schoolId}_${Number(grade)}_${Number(classNum)}`;
  }
  return '';
};

const getPetDocRef = (path: StudentPath, docId: string) =>
  doc(db, `schools/${path.schoolId}/classes/${path.classId}/students/${path.uid}/virtualPet/${docId}`);

const getStudentRef = (path: StudentPath) =>
  doc(db, `schools/${path.schoolId}/classes/${path.classId}/students/${path.uid}`);

const getShareCollectionRef = (path: StudentPath) =>
  collection(db, `schools/${path.schoolId}/classes/${path.classId}/petShares`);

const getDungeonRunsRef = (path: StudentPath) =>
  collection(db, `schools/${path.schoolId}/classes/${path.classId}/students/${path.uid}/virtualPet/dungeon/dungeonRuns`);

const callableError = (error: unknown, fallback: string) => {
  const message = error instanceof Error ? error.message : '';
  return message && message !== 'internal' ? message : fallback;
};

const defaultProfile: PetProfile = {
  name: '별알',
  petForm: 'starEgg',
  level: 1,
  exp: 0,
  intimacy: 20,
  nurtureTraits: { bond: 0, care: 0, courage: 0 },
  abilities: { starlight: 1, vitality: 1, courage: 1 }
};

const defaultStats: PetStats = {
  hunger: 50,
  happiness: 80,
  energy: 70,
  health: 100,
  mood: 'happy',
  lastUpdatedAt: new Date()
};

const defaultInventory: PetInventory = {
  ownedItemIds: [],
  consumables: {},
  equipped: {}
};

const defaultEvolution: PetEvolutionState = {
  dungeonClearCount: 0,
  history: []
};

const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  return undefined;
};

const parseStats = (data: Partial<PetStats>): PetStats => {
  const merged = { ...defaultStats, ...data, lastUpdatedAt: toDate(data.lastUpdatedAt) || new Date() };
  const base: PetStats = {
    ...merged,
    hunger: clamp(merged.hunger),
    happiness: clamp(merged.happiness),
    energy: clamp(merged.energy),
    health: clamp(merged.health),
    mood: merged.mood
  };
  return applyElapsedDecay({ ...base, mood: normalizeMood(base) });
};

const serializeStats = (stats: PetStats) => ({
  hunger: stats.hunger,
  happiness: stats.happiness,
  energy: stats.energy,
  health: stats.health,
  mood: normalizeMood(stats),
  lastUpdatedAt: Timestamp.fromDate(stats.lastUpdatedAt || new Date())
});

export const virtualPetService = {
  async loadClassSettings(path: Pick<StudentPath, 'schoolId' | 'classId'>): Promise<VirtualPetClassSettings> {
    const settingsSnap = await safeGetDoc(
      doc(db, `schools/${path.schoolId}/classes/${path.classId}/virtualPetSettings/config`)
    );
    if (!settingsSnap?.exists()) return DEFAULT_VIRTUAL_PET_SETTINGS;
    const data = settingsSnap.data() as Partial<VirtualPetClassSettings>;
    return {
      enabled: data.enabled !== false,
      freeChatPerDay: Math.max(0, Number(data.freeChatPerDay ?? DEFAULT_VIRTUAL_PET_SETTINGS.freeChatPerDay)),
      freeRestPerDay: Math.max(0, Number(data.freeRestPerDay ?? DEFAULT_VIRTUAL_PET_SETTINGS.freeRestPerDay)),
      freeStrokePerDay: Math.max(0, Number(data.freeStrokePerDay ?? DEFAULT_VIRTUAL_PET_SETTINGS.freeStrokePerDay)),
      interactionCooldownSec: Math.max(0, Number(data.interactionCooldownSec ?? DEFAULT_VIRTUAL_PET_SETTINGS.interactionCooldownSec))
    };
  },

  async resolveStudentPath(params: ResolveStudentPathParams): Promise<StudentPath | null> {
    const { authUid, schoolId, classId: hintClassId, grade, class: classNum } = params;
    if (!authUid || !schoolId) return null;

    const initialClassId = buildClassId(schoolId, grade, classNum, hintClassId);
    if (initialClassId) {
      const directSnap = await safeGetDoc(
        doc(db, `schools/${schoolId}/classes/${initialClassId}/students/${authUid}`)
      );
      if (directSnap?.exists()) {
        return {
          uid: authUid,
          schoolId,
          classId: resolveStudentClassId(directSnap, directSnap.data() as Record<string, unknown>) || initialClassId
        };
      }
    }

    try {
      const uidSnap = await getDocs(
        query(collectionGroup(db, 'students'), where('uid', '==', authUid))
      );
      const match = uidSnap.docs.find((studentDoc) =>
        studentDoc.ref.path.startsWith(`schools/${schoolId}/classes/`)
      );
      if (match) {
        const data = match.data() as Record<string, unknown>;
        const classId = resolveStudentClassId(match, data);
        if (classId) {
          return { uid: match.id, schoolId, classId };
        }
      }
    } catch (error) {
      console.warn('VirtualPet student path lookup failed:', error);
    }

    if (initialClassId) {
      return { uid: authUid, schoolId, classId: initialClassId };
    }
    return null;
  },

  async loadState(path: StudentPath) {
    const [profileSnap, statsSnap, inventorySnap, evolutionSnap, studentSnap] = await Promise.all([
      safeGetDoc(getPetDocRef(path, 'profile')),
      safeGetDoc(getPetDocRef(path, 'stats')),
      safeGetDoc(getPetDocRef(path, 'inventory')),
      safeGetDoc(getPetDocRef(path, 'evolution')),
      safeGetDoc(getStudentRef(path))
    ]);

    let profile = defaultProfile;
    let stats = parseStats(defaultStats);
    let inventory = defaultInventory;
    let evolution = defaultEvolution;

    if (profileSnap?.exists()) {
      const data = profileSnap.data() as Partial<PetProfile>;
      profile = {
        ...defaultProfile,
        ...data,
        nurtureTraits: { ...defaultProfile.nurtureTraits, ...(data.nurtureTraits || {}) },
        abilities: { ...defaultProfile.abilities, ...(data.abilities || {}) },
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt)
      };
      profile = normalizeDailyActions(profile);
    }
    if (statsSnap?.exists()) {
      stats = parseStats(statsSnap.data() as Partial<PetStats>);
    }
    if (inventorySnap?.exists()) {
      inventory = {
        ...defaultInventory,
        ...(inventorySnap.data() as Partial<PetInventory>)
      };
    }
    if (evolutionSnap?.exists()) {
      const data = evolutionSnap.data() as Partial<PetEvolutionState>;
      evolution = {
        dungeonClearCount: data.dungeonClearCount || 0,
        history: (data.history || []).map((item) => ({
          ...item,
          at: toDate(item.at) || new Date()
        })),
        lastEvolvedAt: toDate(data.lastEvolvedAt)
      };
    }

    const studentPoints = studentSnap?.exists() ? Number(studentSnap.data().points || 0) : 0;

    const shouldInitialize =
      !profileSnap?.exists() &&
      !statsSnap?.exists() &&
      !inventorySnap?.exists() &&
      !evolutionSnap?.exists();
    if (shouldInitialize) {
      try {
        await virtualPetService.persistState(path, profile, stats, inventory, evolution);
      } catch (error) {
        console.warn('VirtualPet initial persist failed:', error);
      }
    }

    return {
      profile,
      stats,
      inventory,
      evolution,
      studentPoints,
      migrated: shouldInitialize
    };
  },

  async persistState(path: StudentPath, profile: PetProfile, stats: PetStats, inventory: PetInventory, evolution: PetEvolutionState) {
    const statsToSave = { ...stats, mood: normalizeMood(stats), lastUpdatedAt: stats.lastUpdatedAt || new Date() };
    await Promise.all([
      setDoc(getPetDocRef(path, 'profile'), { ...profile, updatedAt: serverTimestamp() }, { merge: true }),
      setDoc(getPetDocRef(path, 'stats'), serializeStats(statsToSave), { merge: true }),
      setDoc(getPetDocRef(path, 'inventory'), inventory, { merge: true }),
      setDoc(getPetDocRef(path, 'evolution'), { ...evolution, lastEvolvedAt: evolution.lastEvolvedAt || null }, { merge: true })
    ]);
  },

  async buyItem(path: StudentPath, itemId: string) {
    const functions = getFunctions(getApp());
    const spend = httpsCallable<
      { schoolId: string; classId: string; itemId: string },
      { points: number }
    >(functions, 'spendPetShopPoints');
    try {
      const response = await spend({
        schoolId: path.schoolId,
        classId: path.classId,
        itemId,
      });
      return response.data.points;
    } catch (error) {
      throw new Error(callableError(error, '포인트가 부족합니다.'));
    }
  },

  async loadOwnedPetConsumables(path: StudentPath): Promise<OwnedPetConsumable[]> {
    const storeRef = collection(db, `schools/${path.schoolId}/classes/${path.classId}/storeItems`);
    const [studentSnap, storeSnap] = await Promise.all([
      safeGetDoc(getStudentRef(path)),
      getDocs(storeRef).catch(() => null)
    ]);

    const storeEffectById: Record<string, PetItemEffect | undefined> = {};
    storeSnap?.docs.forEach((storeDoc) => {
      const data = storeDoc.data();
      if (data.itemCategory === PET_CONSUMABLE_CATEGORY) {
        storeEffectById[storeDoc.id] = data.petEffect as PetItemEffect | undefined;
      }
    });

    const studentItems = (studentSnap?.data()?.items || {}) as Record<
      string,
      {
        itemId?: string;
        name?: string;
        image?: string;
        itemCategory?: string;
        status?: string;
      }
    >;

    return Object.entries(studentItems)
      .filter(([, value]) => {
        if (!value || value.itemCategory !== PET_CONSUMABLE_CATEGORY) return false;
        if (value.status === 'pending' || value.status === 'used') return false;
        return true;
      })
      .map(([uniqueKey, value]) => ({
        uniqueKey,
        itemId: value.itemId || '',
        name: value.name || '간식',
        image: value.image,
        petEffect: storeEffectById[value.itemId || '']
      }))
      .filter((item) => Boolean(item.itemId));
  },

  async consumePetConsumable(path: StudentPath, uniqueKey: string): Promise<OwnedPetConsumable> {
    const functions = getFunctions(getApp());
    const consume = httpsCallable<
      { schoolId: string; classId: string; inventoryKey: string; kind: 'consumable' },
      { itemId: string; name: string; image: string }
    >(functions, 'consumeOwnedPetItem');
    let consumed: { itemId: string; name: string; image: string };
    try {
      const response = await consume({
        schoolId: path.schoolId,
        classId: path.classId,
        inventoryKey: uniqueKey,
        kind: 'consumable',
      });
      consumed = response.data;
    } catch (error) {
      throw new Error(callableError(error, '간식 사용에 실패했습니다.'));
    }

    const consumedItem: OwnedPetConsumable = {
      uniqueKey,
      itemId: consumed.itemId,
      name: consumed.name,
      image: consumed.image,
    };
    const storeSnap = await safeGetDoc(
      doc(db, `schools/${path.schoolId}/classes/${path.classId}/storeItems`, consumedItem.itemId)
    );
    if (storeSnap?.exists()) {
      const data = storeSnap.data();
      return {
        ...consumedItem,
        petEffect: data.petEffect as PetItemEffect | undefined
      };
    }
    return consumedItem;
  },

  async loadOwnedPlayTickets(path: StudentPath): Promise<OwnedPetPlayTicket[]> {
    const storeRef = collection(db, `schools/${path.schoolId}/classes/${path.classId}/storeItems`);
    const [studentSnap, storeSnap] = await Promise.all([
      safeGetDoc(getStudentRef(path)),
      getDocs(storeRef).catch(() => null)
    ]);

    const storeActionById: Record<string, PetPlayActionType> = {};
    storeSnap?.docs.forEach((storeDoc) => {
      const data = storeDoc.data();
      if (data.itemCategory === PET_PLAY_TICKET_CATEGORY && data.petPlayAction) {
        storeActionById[storeDoc.id] = data.petPlayAction as PetPlayActionType;
      }
    });

    const studentItems = (studentSnap?.data()?.items || {}) as Record<
      string,
      {
        itemId?: string;
        name?: string;
        image?: string;
        itemCategory?: string;
        petPlayAction?: PetPlayActionType;
        status?: string;
      }
    >;

    return Object.entries(studentItems)
      .filter(([, value]) => {
        if (!value || value.itemCategory !== PET_PLAY_TICKET_CATEGORY) return false;
        if (value.status === 'pending' || value.status === 'used') return false;
        return true;
      })
      .map(([uniqueKey, value]) => {
        const action =
          value.petPlayAction || storeActionById[value.itemId || ''] || ('stroke' as PetPlayActionType);
        return {
          uniqueKey,
          itemId: value.itemId || '',
          name: value.name || '놀아주기 이용권',
          image: value.image,
          petPlayAction: action
        };
      })
      .filter((item) => Boolean(item.itemId));
  },

  async consumePlayTicket(path: StudentPath, uniqueKey: string): Promise<void> {
    const functions = getFunctions(getApp());
    const consume = httpsCallable(functions, 'consumeOwnedPetItem');
    try {
      await consume({
        schoolId: path.schoolId,
        classId: path.classId,
        inventoryKey: uniqueKey,
        kind: 'ticket',
      });
    } catch (error) {
      throw new Error(callableError(error, '사용할 수 있는 이용권이 없습니다.'));
    }
  },

  async spendPoints(path: StudentPath, itemId: string) {
    return virtualPetService.buyItem(path, itemId);
  },

  async createShare(path: StudentPath, card: Omit<PetShareCard, 'id' | 'createdAt'>) {
    await addDoc(getShareCollectionRef(path), {
      ...card,
      createdAt: serverTimestamp()
    });
  },

  async listShares(path: StudentPath): Promise<PetShareCard[]> {
    try {
      const snap = await getDocs(getShareCollectionRef(path));
      return snap.docs
      .map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ownerUid: data.ownerUid,
          ownerName: data.ownerName,
          petName: data.petName,
          petForm: data.petForm,
          level: data.level,
          equipped: data.equipped || {},
          createdAt: toDate(data.createdAt) || new Date()
        } as PetShareCard;
      })
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      console.warn('VirtualPet listShares failed:', error);
      return [];
    }
  },

  async startDungeon(path: StudentPath, difficulty: DungeonDifficulty): Promise<PetDungeonRun> {
    const functions = getFunctions(getApp());
    const start = httpsCallable<
      { schoolId: string; classId: string; difficulty: DungeonDifficulty },
      PetDungeonRun & { startedAt: number; endsAt: number }
    >(functions, 'startPetDungeon');
    try {
      const response = await start({
        schoolId: path.schoolId,
        classId: path.classId,
        difficulty,
      });
      const data = response.data;
      return {
        ...data,
        startedAt: new Date(data.startedAt),
        endsAt: new Date(data.endsAt),
      };
    } catch (error) {
      throw new Error(callableError(error, '던전 파견에 실패했어요.'));
    }
  },

  async listDungeonRuns(path: StudentPath): Promise<PetDungeonRun[]> {
    try {
      const snap = await getDocs(getDungeonRunsRef(path));
      return snap.docs
        .map((d) => {
          const data = d.data();
          const endsAt = toDate(data.endsAt) || new Date();
          let status = data.status as PetDungeonRun['status'];
          if (status === 'running' && endsAt.getTime() <= Date.now()) status = 'completed';
          return {
            id: d.id,
            difficulty: data.difficulty,
            startedAt: toDate(data.startedAt) || new Date(),
            endsAt,
            status,
            rewardPoints: Number(data.rewardPoints || 0),
            rewardExp: Number(data.rewardExp || 0)
          } as PetDungeonRun;
        })
        .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
    } catch (error) {
      console.warn('VirtualPet listDungeonRuns failed:', error);
      return [];
    }
  },

  async claimDungeonReward(path: StudentPath, runId: string) {
    const functions = getFunctions(getApp());
    const claim = httpsCallable(functions, 'claimPetDungeonReward');
    try {
      await claim({
        schoolId: path.schoolId,
        classId: path.classId,
        runId,
      });
    } catch (error) {
      throw new Error(callableError(error, '던전 보상 수령에 실패했어요.'));
    }
  }
};

export type { StudentPath };
